import { useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useHistoryStore } from '../../stores/historyStore';
import { t } from '../../i18n';
import styles from './LayersPanel.module.css';

// Import fabric for side effects
import 'fabric';

interface LayerItem {
  id: string;
  name: string;
  type: string;
  visible: boolean;
  locked: boolean;
  object: fabric.Object;
}

const getLayerName = (obj: fabric.Object): string => {
  const type = obj.type || 'object';

  switch (type) {
    case 'textbox':
    case 'i-text':
    case 'text':
      return (obj as fabric.Textbox).text?.substring(0, 20) || 'Text';
    case 'image':
      return 'Image';
    case 'rect':
      return 'Rectangle';
    case 'circle':
      return 'Circle';
    case 'triangle':
      return 'Triangle';
    case 'line':
      return 'Line';
    case 'path':
      return 'Path';
    case 'group':
      return 'Group';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const getLayerIcon = (type: string) => {
  switch (type) {
    case 'textbox':
    case 'i-text':
    case 'text':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="4 7 4 4 20 4 20 7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>
      );
    case 'image':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case 'rect':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      );
    case 'circle':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    case 'triangle':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12 2 22 22 2 22" />
        </svg>
      );
    case 'line':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="20" x2="20" y2="4" />
        </svg>
      );
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      );
  }
};

export const LayersPanel = () => {
  const { canvas } = useCanvas();
  const { selectedObjectIds, setSelection, clearSelection } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();
  const [layers, setLayers] = useState<LayerItem[]>([]);

  // Update layers list when canvas changes
  const updateLayers = useCallback(() => {
    if (!canvas) return;

    const objects = canvas.getObjects();
    const layerItems: LayerItem[] = objects
      .filter((obj) => !obj.isGuide && !obj.isMockup && !obj.isBaseImage && !obj.isMaskOverlay)
      .map((obj) => ({
        id: obj.id || '',
        name: getLayerName(obj),
        type: obj.type || 'object',
        visible: obj.visible !== false,
        locked: !obj.selectable,
        object: obj,
      }))
      .reverse(); // Reverse so top layers appear first

    setLayers(layerItems);
  }, [canvas]);

  useEffect(() => {
    if (!canvas) return;

    updateLayers();

    // Listen for canvas changes
    canvas.on('object:added', updateLayers);
    canvas.on('object:removed', updateLayers);
    canvas.on('object:modified', updateLayers);

    return () => {
      canvas.off('object:added', updateLayers);
      canvas.off('object:removed', updateLayers);
      canvas.off('object:modified', updateLayers);
    };
  }, [canvas, updateLayers]);

  const saveToHistory = useCallback(() => {
    if (!canvas) return;
    const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  const handleLayerClick = (layer: LayerItem) => {
    if (!canvas) return;

    canvas.setActiveObject(layer.object);
    setSelection([layer.id], layer.object);
    canvas.renderAll();
  };

  const handleVisibilityToggle = (layer: LayerItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvas) return;

    const newVisible = !layer.visible;
    layer.object.set('visible', newVisible);
    canvas.renderAll();
    updateLayers();
    saveToHistory();
  };

  const handleMoveUp = (layer: LayerItem) => {
    if (!canvas) return;

    canvas.bringForward(layer.object);
    canvas.renderAll();
    updateLayers();
    saveToHistory();
  };

  const handleMoveDown = (layer: LayerItem) => {
    if (!canvas) return;

    canvas.sendBackwards(layer.object);
    canvas.renderAll();
    updateLayers();
    saveToHistory();
  };

  const handleDelete = (layer: LayerItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvas) return;

    canvas.remove(layer.object);
    clearSelection();
    canvas.renderAll();
    updateLayers();
    saveToHistory();
  };

  if (layers.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelTitle}>{t('design.layers', 'Layers')}</div>
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>{t('design.no_objects', 'No objects yet')}</p>
          <p className={styles.emptyStateHint}>{t('design.no_objects_hint', 'Add text, images, or shapes to get started')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>Layers</div>
      <div className={styles.layersList}>
        {layers.map((layer, index) => (
          <div
            key={layer.id || index}
            className={`${styles.layerItem} ${selectedObjectIds.includes(layer.id) ? styles.selected : ''}`}
            onClick={() => handleLayerClick(layer)}
          >
            <button
              className={`${styles.visibilityButton} ${!layer.visible ? styles.hidden : ''}`}
              onClick={(e) => handleVisibilityToggle(layer, e)}
              title={layer.visible ? 'Hide' : 'Show'}
            >
              {layer.visible ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>

            <span className={styles.layerIcon}>{getLayerIcon(layer.type)}</span>
            <span className={styles.layerName}>{layer.name}</span>

            <div className={styles.layerActions}>
              <button
                className={styles.layerActionButton}
                onClick={(e) => { e.stopPropagation(); handleMoveUp(layer); }}
                title="Move Up"
                disabled={index === 0}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
              <button
                className={styles.layerActionButton}
                onClick={(e) => { e.stopPropagation(); handleMoveDown(layer); }}
                title="Move Down"
                disabled={index === layers.length - 1}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <button
                className={styles.layerActionButton}
                onClick={(e) => handleDelete(layer, e)}
                title="Delete"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LayersPanel;
